import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Card } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import logo from "./logo.png";

function MockInterviewCompleted() {
  const { id } = useParams();

  const [interviewData, setInterviewData] = useState(null);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem("token");
  const profilePhoto = localStorage.getItem("profilePhotoUrl");

  /* ================= FETCH ================= */

  useEffect(() => {
    fetchInterview();
  }, [id]);

  const fetchInterview = async () => {
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/mock-interview/get-single-interview`,
        { id },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (res.data.success) {
        setInterviewData(res.data.data);
      }

      setLoading(false);
    } catch {
      setLoading(false);
    }
  };

  if (loading) return <div>Loading...</div>;
  if (!interviewData)
  return (
    <div className="flex items-center justify-center min-h-[60vh]">

      <div className="max-w-md p-6 text-center border border-red-200 shadow-sm rounded-2xl bg-gradient-to-br from-red-50 to-white">

        {/* ICON */}
        <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 text-red-600 bg-red-100 rounded-full">
          ⚠️
        </div>

        {/* TITLE */}
        <h2 className="text-2xl font-bold text-red-600">
          Unauthorized Access
        </h2>

        {/* MESSAGE */}
        <p className="mt-2 text-sm leading-relaxed text-slate-600">
          You do not have permission to view this interview data. 
          This resource may be restricted or no longer available.
        </p>

        {/* SUBTEXT */}
        <p className="mt-1 text-xs text-slate-500">
          Please contact support or navigate back to your dashboard.
        </p>

        {/* ACTION */}
        <div className="mt-5">
          <button
            onClick={() => window.history.back()}
            className="px-5 py-2 text-sm font-semibold text-white transition-all duration-200 shadow-md rounded-xl bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700"
          >
            ← Go Back
          </button>
        </div>

      </div>

    </div>
  );

  /* ================= HIRING CONFIG ================= */

  const getHiringConfig = (value) => {
    const v = value?.toUpperCase();

    switch (v) {
      case "STRONG HIRE":
        return {
          label: "Strong Hire 🚀",
          color: "text-green-700",
          bg: "bg-green-100",
          border: "border-green-300",
          desc: "Excellent performance. High chances of cracking real interviews.",
        };

      case "HIRE":
        return {
          label: "Hire 👍",
          color: "text-blue-700",
          bg: "bg-blue-100",
          border: "border-blue-300",
          desc: "Good performance. You are close to being job-ready.",
        };

      case "WEAK HIRE":
        return {
          label: "Weak Hire ⚡",
          color: "text-yellow-700",
          bg: "bg-yellow-100",
          border: "border-yellow-300",
          desc: "Moderate performance. Needs improvement before real interviews.",
        };

      case "NO HIRE":
        return {
          label: "No Hire ❌",
          color: "text-red-700",
          bg: "bg-red-100",
          border: "border-red-300",
          desc: "You need significant improvement. Focus on fundamentals.",
        };

      default:
        return {
          label: value || "Unknown",
          color: "text-slate-700",
          bg: "bg-slate-100",
          border: "border-slate-300",
          desc: "No detailed feedback available.",
        };
    }
  };

  const hiring = getHiringConfig(interviewData.ProbabilityOfHiring);

  /* ================= UI ================= */

  return (
    <div className="min-h-screen p-10 bg-slate-50">
      <div className="max-w-6xl mx-auto space-y-6">

        {/* HEADER */}
        <div className="relative flex gap-6 p-8 text-white rounded-lg py-14 bg-gradient-to-r from-blue-800 to-blue-800">

          <img
            src={logo}
            alt="logo"
            className="absolute object-contain h-15 w-14 top-6 right-9 opacity-90"
          />


<div className="flex items-center justify-center">
  <div className="p-[2px] rounded-full bg-gradient-to-r from-blue-500 to-purple-500">
    
    {profilePhoto ? (
      <img
        src={profilePhoto}
        alt="profile"
        className="object-cover w-16 h-16 bg-white rounded-full"
      />
    ) : (
      <div className="flex items-center justify-center w-16 h-16 text-xl font-semibold text-white bg-blue-500 rounded-full">
        {interviewData.StudentName?.[0]}
      </div>
    )}

  </div>
</div>

          <div>
            <h2 className="text-2xl font-bold">
              {interviewData.StudentName}
            </h2>
            <p className="text-sm text-blue-100">
              {interviewData.StudentEmail}
            </p>
          </div>
        </div>

        {/* HIRING CARD */}
        <Card
          className={`text-center rounded-xl shadow-sm border ${hiring.border} ${hiring.bg}`}
        >
          <p className="mb-2 text-sm text-slate-600">
            Hiring Evaluation
          </p>

          <h1 className={`text-3xl font-extrabold mb-2 ${hiring.color}`}>
            {hiring.label}
          </h1>

          <p className="text-sm leading-relaxed text-slate-700">
            {hiring.desc}
          </p>
        </Card>

        {/* SCORE + RESULT */}
        <div className="grid grid-cols-2 gap-6">
          <Card className="text-center bg-blue-100">
            <p className="text-xl font-bold text-slate-900">Score</p>
            <h1 className="text-2xl font-bold">
              {interviewData.Score}
            </h1>
          </Card>

          <Card className="flex flex-col items-center justify-center text-center bg-blue-100">
            <h2 className="text-xl font-bold text-slate-900">
              Interview Result
            </h2>
            <h2 className="text-2xl font-bold text-green-700">
              {interviewData.InterviewResult || "Not Given By Instructor"}
            </h2>
          </Card>

          <Card className="flex flex-col items-center justify-center text-center bg-blue-100">
            <h2 className="text-xl font-bold text-red-700">
              Interview Feedback
            </h2>
            <h2 className="font-semibold text-md text-slate-600">
              {interviewData.TechnicalFeedback || "No remarks"}
            </h2>
          </Card>
        </div>

        {/* DETAILS */}
        <Card className="transition duration-300 border border-black shadow-sm bg-slate-100 rounded-2xl hover:shadow-md">

          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">

            <div className="flex items-center gap-3">
              <div className="p-2 text-blue-600 bg-blue-100 rounded-lg">
                👨‍🏫
              </div>
              <div>
                <p className="text-xs text-slate-500">Feedback given by:</p>
                <p className="font-semibold text-slate-800">
                  {interviewData.InstructorName}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="p-2 text-purple-600 bg-purple-100 rounded-lg">
                📅
              </div>
              <div>
                <p className="text-xs text-slate-500">Date</p>
                <p className="font-semibold text-slate-800">
                  {dayjs(interviewData.ScheduledDate).format("DD MMM YYYY")}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="p-2 text-green-600 bg-green-100 rounded-lg">
                ⏰
              </div>
              <div>
                <p className="text-xs text-slate-500">Time</p>
                <p className="font-semibold text-slate-800">
                  {dayjs(interviewData.ScheduledTime, "HH:mm").format("hh:mm A")}
                </p>
              </div>
            </div>

          </div>
        </Card>

      </div>
    </div>
  );
}

export default MockInterviewCompleted;